import { BaseLandlordSurvey } from "api/landlord-surveys/types/types";
import { Card } from "shared/components/Card";
import { DateTime } from "luxon";
import { Badge, BadgeSize, BadgeVariant, Show } from "shared/components";
import {
  ContextMenuPopover,
  type Action
} from "shared/components/ContextMenuPopover";
import { useMemo, FC } from "react";
import { useNavigate } from "react-router-dom";

type SurveyCardProps = BaseLandlordSurvey & { surveysCount: number };

export const SurveyCard: FC<SurveyCardProps> = (props) => {
  const { surveysCount, ...survey } = props;
  const navigate = useNavigate();

  const actions = useMemo((): Action[] => {
    const list: Action[] = [];
    if (!survey.is_default)
      list.push({
        title: "Make default",
        onClick: () => {}
      });
    list.push(
      {
        title: "Edit",
        onClick: () => {
          navigate(`/landlord-surveys/${survey.identifier}`);
        }
      },
      {
        title: "Duplicate",
        onClick: () => {}
      }
    );
    if (surveysCount > 1)
      list.push({
        title: "Delete",
        onClick: () => {},
        className: "text-danger-500"
      });
    return list;
  }, [navigate, survey.identifier, survey.is_default, surveysCount]);

  const updatedAtText = DateTime.fromISO(survey.updated_at).toFormat(
    "MMM d, yyyy"
  );

  return (
    <Card
      className="flex h-full min-h-0 w-full min-w-0 flex-col"
      linkTo={`/landlord-surveys/${survey.identifier}`}
    >
      <header className="flex w-full shrink-0 items-center justify-between gap-2 border-b border-alpha-40 px-6 py-3">
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
          <h2 className="truncate text-base font-semibold text-content-1000">
            {survey.name}
          </h2>
          <Show if={survey.is_default}>
            <Badge
              size={BadgeSize.SM}
              className="shrink-0 !rounded-md"
              variant={BadgeVariant.OUTLINE}
            >
              Default
            </Badge>
          </Show>
        </div>
        <div className="shrink-0">
          <ContextMenuPopover
            borderLess
            actions={actions}
            triggerClassName="translate-x-3"
          />
        </div>
      </header>
      <footer className="flex shrink-0 items-center justify-between gap-2 px-6 py-3">
        <Badge
          className="shrink-0"
          size={BadgeSize.BASE}
          variant={BadgeVariant.PRIMARY}
        >
          {survey.questions_count}{" "}
          {survey.questions_count === 1 ? "question" : "questions"}
        </Badge>
        <span className="text-xs font-medium text-content-300">
          {updatedAtText}
        </span>
      </footer>
    </Card>
  );
};
